import type { FC } from 'react';
import { ConfirmationModal } from '@components/ConfirmationModal';
import IconMdi from '@components/IconMdi';
import { useEntityStore } from '@ContractBuilder/store';
import { authProvider } from '@helpers/auth';
import { mdiOpenInNew } from '@mdi/js';
import Nav from '@root/src/routes';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  isBlock: boolean;
  onCancel: () => void;
  onSaveChanges: () => void | Promise<false | void>;
  usedInTemplates?: Array<{
    id: string;
    name?: string;
  }>;
}

export const DeactivateBlockConfirmationModal: FC<UnsavedChangesModalProps> = ({
  isOpen,
  isBlock,
  onCancel,
  onSaveChanges,
  usedInTemplates = [],
}) => {
  const { isLoading } = useEntityStore(({ isLoading }) => ({ isLoading }));

  const handleLinkClick = async (templateId: string) => {
    const searchParams = await authProvider.getAuthSearchParams();
    const win = window.open(`${Nav.TemplateContract.replace(':id', templateId)}${searchParams}`);
    return win?.focus();
  };

  const entityType = isBlock ? 'block' : 'clause';
  const hasLinkedTemplates = usedInTemplates.length > 0;

  const content = hasLinkedTemplates
    ? `Deactivating this ${entityType} will remove its effective dates. It can no longer be added to templates or contracts but remains in the following templates:`
    : `Deactivating this ${entityType} will remove its effective dates. It can no longer be added to templates or contracts.`;

  return (
    <ConfirmationModal
      data-testid="block-deactivation-modal"
      confirmLabel="Ok"
      description={content}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onCancel}
      onConfirm={onSaveChanges}
      title={`Deactivate this ${entityType}?`}
    >
      <ul className="text-info-500 mt-6 flex max-h-[320px] cursor-default flex-col items-start gap-1 overflow-auto text-sm">
        {usedInTemplates.map(({ id, name }) => (
          <div key={id} className="w-full py-1 pr-3">
            <button
              className="text-primary-600 flex w-full cursor-pointer justify-between"
              onClick={() => handleLinkClick(id)}
            >
              <p className="truncate text-xs no-underline hover:underline">{name}</p>
              <IconMdi
                className="ml-1"
                path={mdiOpenInNew}
                size={0.6}
                data-testid="block-icon-indicator:linked-library-block"
              />
            </button>
          </div>
        ))}
      </ul>
    </ConfirmationModal>
  );
};
